import React from 'react';
import { useModal } from 'hooks/useModal';
import { ModalsKeys } from 'global';
import {
  Button,
  Dialog,
  DialogContent,
  Box,
  Chip,
  Typography,
  Grid2,
  DialogActions,
  CircularProgress,
  Stack,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { S3_BUCKET_NAME } from 'config';
import moment from 'moment';
import { DateFormats } from 'constants/common';
import { UploadedFileView } from 'components/shared/UploadedFileView/UploadedFileView';
import { useQuery } from '@apollo/client';
import { broadcastApi } from 'modules/broadcasts/api';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { BroadcastByIdResponse, BroadcastByIdVariables } from 'modules/broadcasts/api/types';
import { BroadcastTypeChip } from '../components';

export const ViewBroadcastDialog = () => {
  const {
    handleClose,
    isOpen,
    data: { broadcastPreview, broadcastId },
  } = useModal(ModalsKeys.ViewBroadcastDialog);

  const { data, loading, error } = useQuery<BroadcastByIdResponse, BroadcastByIdVariables>(
    broadcastApi.getBroadcastById,
    {
      skip: !broadcastId,
      variables: {
        broadcastId: broadcastId!,
      },
    }
  );

  const broadcast = broadcastPreview || data?.broadcast?.broadcastById;

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogContent>
        <RenderWithFallback
          isLoading={loading}
          error={error}
          fallBack={
            <Stack alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Stack>
          }
        >
          {broadcast && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {broadcast.account?.logo && (
                  <Box sx={{ mr: 2 }}>
                    <img
                      src={`https://${S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${broadcast.account.logo}`}
                      alt={broadcast.account?.name!}
                      style={{ borderRadius: '50%', width: '60px', height: '60px' }}
                    />
                  </Box>
                )}
                <Box flexGrow={1}>
                  <Typography variant="h6" component="p">
                    {broadcast.account?.name}
                  </Typography>
                  <Chip
                    icon={<CalendarTodayIcon />}
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Effective Date: {moment(broadcast.effectiveDateTime).format(DateFormats.Short)}
                      </Typography>
                    }
                    variant="outlined"
                    sx={{ bgcolor: 'grey.100', padding: '2px 4px' }}
                    size="small"
                  />
                </Box>
                <Box alignSelf="flex-start">
                  {/*                   <Typography variant="body2" component="span" color="text.secondary" sx={{ mt: 0.5 }}>
                    {moment(broadcast.createdDateTime).format(DateFormats.Short)}
                  </Typography> */}
                  <BroadcastTypeChip type={broadcast.type} />
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" component="p">
                  {broadcast.title}
                </Typography>
                <Box dangerouslySetInnerHTML={{ __html: broadcast.body ?? '' }}></Box>
              </Box>
              {broadcast.attachments?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" component="span">
                    Attachments
                  </Typography>
                  <Grid2 container spacing={2} sx={{ mt: 1 }}>
                    {broadcast.attachments.map((attachment, idx) => (
                      <Grid2 key={idx} size={6}>
                        <UploadedFileView
                          onDownload={() =>
                            window.open(`https://${S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${attachment.source}`)
                          }
                          name={attachment.name}
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
              )}
            </>
          )}
        </RenderWithFallback>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="outlined" size="small">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
